import Box from "@mui/joy/Box";
import Can, { AbilityContext } from "../../casl/Can";
import ColorSchemeToggle from "./color-scheme-toggle";
import Divider from "@mui/joy/Divider";
import GlobalStyles from "@mui/joy/GlobalStyles";
import IconButton from "@mui/joy/IconButton";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemButton from "@mui/joy/ListItemButton";
import ListItemContent from "@mui/joy/ListItemContent";
import ListItemDecorator from "@mui/joy/ListItemDecorator";
import NotificationIcon from "../notifications/notification-icon";
import React, { useContext, useState , useEffect } from "react";
import Sheet from "@mui/joy/Sheet";
import Typography from "@mui/joy/Typography";
import useScript from "../../utils/useScript";
import useSocketStore from "../../stores/store-socket";
import { closeSidebar } from "./utils";
import {userRole, clientUserId, userFullName, userRoleKey } from "../../utils/helper";
import { KeyboardArrowUp } from "@mui/icons-material";
import { Link , useLocation} from "react-router-dom";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { socketLogout } from "../../configs/socket";
import { useSignOut } from "react-auth-kit";
import useBreadcrumbStore from "../../stores/store-breadcrumbs";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import aqualogo from "../../assets/images/aqualogo.png";
import { RolesTypeEnum, RolesModel } from "../../models/roles/roles.model";
import { useFetchBuildingsWithPlants } from "../../api/buildings";
import { useFetchRolesWithoutPagination } from "../../api/roles";
import { useQueryClient } from "@tanstack/react-query";
/* eslint-disable jsx-a11y/anchor-is-valid */

const useEnhancedEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/**
 * Sidebar Navigation Component
 * 
 * Provides the main navigation menu for the application. The sidebar can be collapsed/expanded
 * and highlights the currently active route. It uses Material-UI's Drawer component for the base structure.
 * 
 * Features:
 * - Collapsible sidebar with persistent state
 * - Dynamic menu items based on user permissions
 * - Active route highlighting
 * - Responsive design (collapses automatically on mobile)
 * - Nested menu items support
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls whether the sidebar is expanded or collapsed
 * @param {Function} props.onClose - Callback function to handle sidebar close events
 * @param {boolean} props.isMobile - Flag indicating if the current view is mobile
 * 
 * @example
 * ```tsx
 * <Sidebar 
 *   open={sidebarOpen}
 *   onClose={() => setSidebarOpen(false)}
 *   isMobile={false}
 * />
 * ```
 */
const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isCollapsedDownload, setIsCollapsedDownload] = useState(true);
  const navigate: NavigateFunction = useNavigate();
  const status = useScript(`https://unpkg.com/feather-icons`);
  const signOut = useSignOut();
  const queryClient = useQueryClient();

  const name = userFullName;
  const roleId = userRole; // This is the role ID from cookie
  const { socketRefCurrentState } = useSocketStore();
  const profilePath = `/clients/${clientUserId}`;
  const ability = useContext(AbilityContext);

  // Fetch roles to get role key from role ID
  const { data: rolesData } = useFetchRolesWithoutPagination();
  const allRoles = rolesData?.data;
  
  // Find the current role and get its key
  const currentRole = allRoles?.find((role: RolesModel) => role.id === roleId);
  const currentRoleKey = currentRole?.key;
  
  // Define role groups for cleaner conditionals
  const clientRoles = [RolesTypeEnum.Client, RolesTypeEnum.ClientWorker];
  const isClientManager = currentRoleKey === RolesTypeEnum.ClientManager;

  const location = useLocation();
  const [index, setIndex] = useState(-1);

  useEffect(() => {
    // Map route patterns to their respective indexes
    const routeToIndex = {
      "/dashboard": 0,
      "/clients": 4,
      "/products": 5,
      "/roles": 6,
      "/users": 7,
      "/activity": 8,
      [profilePath]: 10, // Handle dynamic profile path
      "/acintern-download": 11,
      "/kunden-downloads": 12,
      "/c3-instructions": 13,
      "/settings": 14,
      "/impressum": 15,
      "/drinking-water-tasks": 16,
      "/all-drinking-water-buildings": 17,
      "/task-search": 18,
    };

    // Check for partial matches for nested routes
    const nestedRouteToIndex = [
      { pattern: /^\/clients/, index: 4 },
      { pattern: /^\/products/, index: 5 },
      { pattern: /^\/roles/, index: 6 },
      { pattern: /^\/users/, index: 7 }, 
      { pattern: /^\/activity/, index: 8 },
      { pattern: /^\/drinking-water-tasks/, index: 16 },
      { pattern: /^\/task-search/, index: 18 },
    ];
    // Check exact matches first
    let selectedIndex = routeToIndex[location.pathname];

    // Check for nested route matches if no exact match is found
    if (selectedIndex === undefined) {
      for (const { pattern, index } of nestedRouteToIndex) {
        if (pattern.test(location.pathname)) {
          selectedIndex = index;
          break;
        }
      }
    }

    setIndex(selectedIndex ?? -1);
  }, [location.pathname, profilePath]);

  const handleLogout = () => {
    try {
      if (socketRefCurrentState) {
        // Close the WebSocket connection
        socketRefCurrentState.close();
      }

      // This set time out is used to fix a problem related to auth library when sign out
      // Https://github.com/react-auth-kit/react-auth-kit/issues/1193
      setTimeout(() => {
        signOut();

        // Clear React Query cache so next user gets fresh dashboard data
        queryClient.clear();

        // Clear user storage
        localStorage.clear();
        sessionStorage.clear();

        navigate("/login", { replace: true });
      }, 500);

      socketLogout();
    } catch (error) {
      // Handle any errors that might occur during the logout process
      console.error("Error during logout:", error);
    }
  };

  useEnhancedEffect(() => {
    // Feather icon setup: https://github.com/feathericons/feather#4-replace
    // @ts-ignore
    if (typeof feather !== "undefined") {
      // @ts-ignore
      feather.replace();
    }
  }, [status]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };
  const toggleCollapseDownload = () => {
    setIsCollapsedDownload(!isCollapsedDownload);
  };

  const resetBreadcrumb = useBreadcrumbStore(
    (state: any) => state.resetBreadcrumb
  );

  const clientLabel = currentRoleKey === RolesTypeEnum.ClientManager ? 'Standorte': 'Kunden';
  const { data: buildingsData } = useFetchBuildingsWithPlants(clientUserId, {
    enabled: !!clientUserId,
  });
  const buildings = buildingsData?.data ?? [];
  const shouldShowMyProfile = userRoleKey === RolesTypeEnum.Client || userRoleKey === RolesTypeEnum.ClientWorker || userRoleKey === RolesTypeEnum.ClientUnpaid;
  const [openBuildings, setOpenBuildings] = useState<Record<string, boolean>>({});

  const toggleBuilding = (id: string) => {
    setOpenBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "[data-feather], .feather": {
            color: "var(--Icon-color)",
            margin: "var(--Icon-margin)",
            fontSize: "var(--Icon-fontSize, 20px)",
            width: "1em",
            height: "1em",
          },
        }}
      />
      <Sheet
        className="Sidebar"
        sx={{
          bgcolor: "background.body",
          position: {
            xs: "fixed",
            lg: "sticky",
          },
          transform: {
            xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))",
            lg: "none",
          },
          transition: "transform 0.4s, width 0.4s",
          zIndex: 10000,
          height: "100dvh",
          width: "var(--Sidebar-width)",
          top: 0,
          px: 1.5,
          py: 2,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <GlobalStyles
          styles={(theme) => ({
            ":root": {
              "--Sidebar-width": "224px",
              [theme.breakpoints.up("lg")]: {
                "--Sidebar-width": "256px",
              },
            },
          })}
        />
        <Box
          className="Sidebar-overlay"
          sx={{
            position: "fixed",
            zIndex: 9998,
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "background.body",
            opacity: "calc(var(--SideNavigation-slideIn, 0) - 0.2)",
            transition: "opacity 0.4s",
            transform: {
              xs: "translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))",
              lg: "translateX(-100%)",
            },
          }}
          onClick={() => closeSidebar()}
        />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <img
            src={aqualogo} // Replace with the path to your logo image
            alt="Logo"
            style={{
              maxHeight: 30,
              maxWidth: "100%",
              marginRight: 1,
              padding: 3,
            }}
          />

          <ColorSchemeToggle sx={{ ml: "auto" }} />
          <NotificationIcon />
        </Box>
        <Box
          sx={{
            minHeight: 0,
            overflow: "hidden auto",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <List
            sx={{
              "--ListItem-radius": "8px",
              "--List-gap": "4px",
              "--List-nestedInsetStart": "40px",
            }}
          >
            <ListItem>
              <ListItemButton
                selected={index === 0}
                onClick={() => {
                  setIndex(0);
                  resetBreadcrumb();
                }}
                role="menuitem"
                component={Link}
                to="/dashboard"
              >
                <ListItemDecorator>
                  <i data-feather="home" />
                </ListItemDecorator>
                <ListItemContent>Home</ListItemContent>
              </ListItemButton>
            </ListItem>
            {/* <ListItem>
              <ListItemButton
                selected={index === 1}
                onClick={() => {
                  setIndex(1);
                  resetBreadcrumb();
                }}
                role="menuitem"
                component={Link}
                to="/messenger"
              >
                <ListItemDecorator>
                  <i data-feather="message-square" />
                </ListItemDecorator>
                <ListItemContent>Nachrichten</ListItemContent>
              </ListItemButton>
            </ListItem> */}
            {(ability.can("view", "All Clients") || ability.can("view", "Assigned Clients") || ability.can("view", "Iw Clients") || ability.can("view", "Tw Clients")) &&
              <ListItem>
                <ListItemButton
                  selected={index === 4}
                  onClick={() => {
                    setIndex(4);
                    resetBreadcrumb();
                  }}
                  role="menuitem"
                  component={Link}
                  to="/clients"
                >
                  <ListItemDecorator>
                    <i data-feather="users" />
                  </ListItemDecorator>
                  <ListItemContent>{clientLabel}</ListItemContent>
                </ListItemButton>
              </ListItem>}
            {!clientRoles.includes(currentRoleKey as RolesTypeEnum)
              && <Can I="view" a="Products">
                <ListItem>
                  <ListItemButton
                    selected={index === 5}
                    onClick={() => {
                      setIndex(5);
                      resetBreadcrumb();
                    }}
                    role="menuitem"
                    component={Link}
                    to="/products"
                  >
                    <ListItemDecorator>
                      <LocalMallIcon />
                    </ListItemDecorator>
                    <ListItemContent>Produkte</ListItemContent>
                  </ListItemButton>
                </ListItem>
              </Can>}
            {(ability.can("view", "Roles") || ability.can("view", "All Users") || ability.can("view", "Assigned Users") || isClientManager) && (
              <ListItem nested>
                <ListItemButton onClick={toggleCollapse}>
                  <ListItemDecorator>
                    <i data-feather="shield" />
                  </ListItemDecorator>
                  <ListItemContent>Verwaltung</ListItemContent>
                  <KeyboardArrowUp
                    style={{
                      transform: isCollapsed ? "rotate(-180deg)" : "none",
                    }}
                  />
                </ListItemButton>
                <List sx={{ display: isCollapsed ? "none" : "block" }}>
                  <Can I="view" a="Roles">
                    <ListItem>
                      <ListItemButton
                        selected={index === 6}
                        onClick={() => {
                          setIndex(6);
                          resetBreadcrumb();
                        }}
                        role="menuitem"
                        component={Link}
                        to="/roles"
                        variant="soft"
                      >
                        Rolle
                      </ListItemButton>
                    </ListItem>
                  </Can>
                  {(ability.can("view", "All Users") || ability.can("view", "Assigned Users") || isClientManager)&&
                    <ListItem>
                      <ListItemButton
                        selected={index === 7}
                        onClick={() => {
                          setIndex(7);
                          resetBreadcrumb();
                        }}
                        role="menuitem"
                        component={Link}
                        to={isClientManager ? "/users/clients" : "/users"}
                        variant="soft"
                      >
                        Benutzer
                      </ListItemButton>
                    </ListItem>
                  }
                </List>
              </ListItem>
            )}
            <Can I="view" a="Task">
              <ListItem>
                <ListItemButton
                  selected={index === 8}
                  onClick={() => {
                    resetBreadcrumb();
                    setIndex(8);
                  }}
                  role="menuitem"
                  component={Link}
                  to="/activity"
                >
                  <ListItemDecorator>
                    <i data-feather="briefcase" />
                  </ListItemDecorator>
                  <ListItemContent>Zugeteilte Aufgaben</ListItemContent>
                </ListItemButton>
              </ListItem>
            </Can>
            {shouldShowMyProfile && (
              <ListItem>
                <ListItemButton
                  selected={index === 10}
                  onClick={() => {
                    setIndex(10);
                    resetBreadcrumb();
                  }}
                  role="menuitem"
                  component={Link}
                  to={profilePath}
                >
                  <ListItemDecorator>
                    <i data-feather="user" />
                  </ListItemDecorator>
                  <ListItemContent>Mein Profil</ListItemContent>
                </ListItemButton>
              </ListItem>
            )}

            {clientRoles.includes(currentRoleKey as RolesTypeEnum) && buildings?.length > 0 && (
              <>
                {buildings.map((building) => (
                  <ListItem nested key={building.id}>
                    <ListItemButton onClick={() => toggleBuilding(building.id)}>
                      <ListItemDecorator>
                        <i data-feather="archive" />
                      </ListItemDecorator>
                      <ListItemContent>{building.name}</ListItemContent>
                      <KeyboardArrowUp
                        style={{
                          transform: openBuildings[building.id] ? "rotate(0deg)" : "rotate(-180deg)",
                          transition: "transform 0.2s",
                        }}
                      />
                    </ListItemButton>

                    <List sx={{ display: openBuildings[building.id] ? "block" : "none", pl: 3 }}>
                      {building.plants?.map((plant) => (
                        <ListItem key={plant.id}>
                          <ListItemButton
                            onClick={() => navigate(`clients/${clientUserId}/buildings/${building.id}/plants/${plant.id}`)}
                            variant="soft"
                            sx={() => ({
                              px: 2,
                              py: 1,
                            })}
                          >
                            <ListItemContent>{plant.name}</ListItemContent>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </ListItem>
                ))}
              </>
            )}

            <Can I="view" a="Drinking Water Tasks">
              <ListItem>
                <ListItemButton
                  selected={index === 16}
                  onClick={() => {
                    setIndex(16);
                    resetBreadcrumb();
                  }}
                  role="menuitem"
                  component={Link}
                  to="/drinking-water-tasks"
                >
                  <ListItemDecorator>
                    <i data-feather="droplet" />
                  </ListItemDecorator>
                  <ListItemContent>Trinkwasser Aufgaben</ListItemContent>
                </ListItemButton>
              </ListItem>
            </Can>

            <Can I="view" a="Task Search">
              <ListItem>
                <ListItemButton
                  selected={index === 18}
                  onClick={() => {
                    setIndex(18);
                    resetBreadcrumb();
                  }}
                  role="menuitem"
                  component={Link}
                  to="/task-search"
                >
                  <ListItemDecorator>
                    <i data-feather="search" />
                  </ListItemDecorator>
                  <ListItemContent>Aufgabensuche</ListItemContent>
                </ListItemButton>
              </ListItem>
            </Can>

            <Can I="view" a="All Drinking Water Buildings">
              <ListItem>
                <ListItemButton
                  selected={index === 17}
                  onClick={() => {
                    setIndex(17);
                    resetBreadcrumb();
                  }}
                  role="menuitem"
                  component={Link}
                  to="/all-drinking-water-buildings"
                >
                  <ListItemDecorator>
                    <i data-feather="table" />
                  </ListItemDecorator>
                  <ListItemContent>Liegenschaftsuche</ListItemContent>
                </ListItemButton>
              </ListItem>
            </Can>
            
            {(ability.can('view', 'Download Acintern') || ability.can('view', 'Download Kunden')) && (
              <ListItem nested>
                <ListItemButton onClick={toggleCollapseDownload}>
                  <ListItemDecorator>
                    <i data-feather="download" />
                  </ListItemDecorator>
                  <ListItemContent>Download Area</ListItemContent>
                  <KeyboardArrowUp
                    style={{
                      transform: isCollapsedDownload ? "rotate(-180deg)" : "none",
                    }}
                  />
                </ListItemButton>
                <List sx={{ display: isCollapsedDownload ? "none" : "block" }}>
                  <Can I="view" a="Download Acintern">
                    <ListItem>
                      <ListItemButton
                        selected={index === 11}
                        onClick={() => {
                          setIndex(11);
                          resetBreadcrumb();
                        }}
                        role="menuitem"
                        component={Link}
                        to="/acintern-download"
                        variant="soft"
                      >
                        AC intern
                      </ListItemButton>
                    </ListItem>
                  </Can>
                  <Can I="view" a="Download Kunden">
                    <ListItem>
                      <ListItemButton
                        selected={index === 12}
                        onClick={() => {
                          setIndex(12);
                          resetBreadcrumb();
                        }}
                        role="menuitem"
                        component={Link}
                        to="/kunden-downloads"
                        variant="soft"
                      >
                        Kunden Downloads
                      </ListItemButton>
                    </ListItem>
                  </Can>
                  <Can I="view" a="Download Kunden">
                    <ListItem>
                      <ListItemButton
                        selected={index === 13}
                        onClick={() => {
                          setIndex(13);
                          resetBreadcrumb();
                        }}
                        role="menuitem"
                        component={Link}
                        to="/c3-guide"
                        variant="soft"
                      >
                        c3 Anleitungen
                      </ListItemButton>
                    </ListItem>
                  </Can>
                </List>
              </ListItem>
            )}

          </List>
          <List
            sx={{
              mt: "auto",
              flexGrow: 0,
              "--ListItem-radius": "8px",
              "--List-gap": "8px",
            }}
          >
            <ListItem>
              <ListItemButton
                selected={index === 14}
                onClick={() => {
                  setIndex(14);
                  resetBreadcrumb();
                }}
                role="menuitem"
                component={Link}
                to="/settings"
              >
                <ListItemDecorator>
                  <i data-feather="settings" />
                </ListItemDecorator>
                <ListItemContent>Einstellungen</ListItemContent>
              </ListItemButton>
            </ListItem>
            <ListItem>
              <ListItemButton
                selected={index === 15}
                onClick={() => {
                  setIndex(15);
                  resetBreadcrumb();
                }}
                role="menuitem"
                component={Link}
                to="/impressum"
              >
                <ListItemDecorator>
                  <i data-feather="info" />
                </ListItemDecorator>
                <ListItemContent>Impressum</ListItemContent>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
        <Divider />
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontSize="sm" fontWeight="lg">
              {name}
            </Typography>
          </Box>
          <IconButton onClick={handleLogout} variant="plain" color="neutral">
            <i data-feather="log-out" />
          </IconButton>
        </Box>
      </Sheet>
    </>
  );
}

export default Sidebar;
